// src/components/CardFacturation.jsx
import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  alpha,
} from "@mui/material";

import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";
import LocalParkingRoundedIcon from "@mui/icons-material/LocalParkingRounded";
import LocalFloristRoundedIcon from "@mui/icons-material/LocalFloristRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

const bichon = {
  // base neutra (la dejé porque te sostiene legibilidad)
  bg: "#E5E1D0",
  paper: "#F7F3EC",
  text: "#102A2E",
  muted: "#5E6B6E",
  border: "#D9D2C7",

  // 🎨 colores marca Bichón
  blue: "#27276D",
  teal: "#20AB8A",
  yellow: "#CFC54A",
  pink: "#C72E64",
};

const benefits = [
  {
    icon: <PlaceOutlinedIcon />,
    title: "Ubicación privilegiada",
    description:
      "Sobre la avenida principal de Pichilemu, restaurantes y comercios cercanos. A 300 metros de la playa.",
    tone: "teal",
  },
  {
    icon: <ScheduleRoundedIcon />,
    title: "Horarios flexibles",
    description: "Check-in 14:00 hs. Check-out 12:00 hs.",
    tone: "yellow",
  },
  {
    icon: <CelebrationRoundedIcon />,
    title: "Eventos en el hostal",
    description: "Experiencias exclusivas frecuentes para nuestros clientes.",
    tone: "pink",
  },
  {
    icon: <LocalParkingRoundedIcon />,
    title: "Estacionamiento privado",
    description:
      "Podés guardar tu vehículo en un lugar seguro, dentro del mismo hostal.",
    tone: "yellow",
  },
  {
    icon: <LocalFloristRoundedIcon />,
    title: "Servicio personalizado",
    description:
      "Nos importa cómo esperás ser tratado/a: aprendemos tus preferencias y nos adaptamos a tu forma de comunicarte.",
    tone: "teal",
  },
];

const CardFacturation = () => {
  const toneColor = (tone) => {
    if (tone === "yellow") return bichon.yellow;
    if (tone === "pink") return bichon.pink;
    return bichon.teal; // default
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "80vh",
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${alpha(bichon.blue, 0.18)}`,
        background: `
          radial-gradient(900px 280px at 18% 0%, ${alpha(bichon.yellow, 0.22)} 0%, transparent 60%),
          radial-gradient(720px 280px at 105% 12%, ${alpha(bichon.teal, 0.20)} 0%, transparent 55%),
          radial-gradient(680px 320px at 60% 105%, ${alpha(bichon.pink, 0.14)} 0%, transparent 60%),
          linear-gradient(180deg, ${alpha(bichon.paper, 0.98)} 0%, ${alpha(
          bichon.bg,
          0.98,
        )} 100%)
        `,
        boxShadow: "0 18px 60px rgba(0,0,0,0.22)",
        backdropFilter: "blur(10px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 2.4,
          pb: 2,
          borderBottom: `1px solid ${alpha(bichon.blue, 0.12)}`,
          display: "flex",
          alignItems: "center",
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 999,
            display: "grid",
            placeItems: "center",
            background: alpha(bichon.yellow, 0.26),
            border: `1px solid ${alpha(bichon.yellow, 0.55)}`,
            boxShadow: "0 10px 22px rgba(0,0,0,0.10)",
            flex: "0 0 auto",
            "& svg": { color: bichon.blue },
          }}
        >
          <StarRoundedIcon />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"DynaPuff", cursive',
              fontSize: "1.55rem",
              lineHeight: 1.05,
              color: bichon.blue,
              letterSpacing: 0.2,
            }}
          >
            Beneficios de tu estadía
          </Typography>
          <Typography
            sx={{
              color: alpha(bichon.blue, 0.78),
              mt: 0.4,
              fontSize: "0.95rem",
              fontWeight: 600,
            }}
          >
            Lo que hace que Bichón se sienta distinto.
          </Typography>
        </Box>
      </Box>

      {/* Body scrollable */}
      <Box
        sx={{
          p: 2.25,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pr: 1,

          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: bichon.pink,
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: bichon.blue },
        }}
      >
        <List sx={{ m: 0, p: 0 }}>
          {benefits.map((b, index) => {
            const c = toneColor(b.tone);

            return (
              <React.Fragment key={b.title}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    borderRadius: 3,
                    background: alpha("#fff", 0.62),
                    border: `1px solid ${alpha(bichon.blue, 0.14)}`,
                    boxShadow: "0 10px 26px rgba(0,0,0,0.08)",
                    mb: 1.25,
                    py: 1.25,
                    position: "relative",
                    overflow: "hidden",

                    // barra de color “dopamina” a la izquierda
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 6,
                      background: `linear-gradient(180deg, ${c} 0%, ${bichon.blue} 140%)`,
                      opacity: 0.95,
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 56 }}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        backgroundColor: alpha(c, 0.16),
                        color: bichon.blue,
                        border: `1px solid ${alpha(c, 0.40)}`,
                        boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
                        "& svg": { color: bichon.blue },
                      }}
                    >
                      {b.icon}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: 950,
                          color: bichon.blue,
                          fontSize: "1.02rem",
                          lineHeight: 1.15,
                        }}
                      >
                        {b.title}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{
                          mt: 0.35,
                          color: alpha(bichon.blue, 0.72),
                          fontSize: "0.95rem",
                          lineHeight: 1.35,
                          fontWeight: 600,
                        }}
                      >
                        {b.description}
                      </Typography>
                    }
                  />
                </ListItem>

                {index < benefits.length - 1 && (
                  <Divider sx={{ my: 1, borderColor: alpha(bichon.blue, 0.10) }} />
                )}
              </React.Fragment>
            );
          })}
        </List>

        <Box
          sx={{
            mt: 1.25,
            p: 1.1,
            borderRadius: 2.2,
            background: alpha(bichon.teal, 0.14),
            border: `1px solid ${alpha(bichon.teal, 0.28)}`,
            color: bichon.blue,
            fontWeight: 900,
            fontSize: "0.92rem",
          }}
        >
          Tip: si necesitás recomendaciones de restaurantes o lugares, pregúntanos ✨
        </Box>
      </Box>
    </Box>
  );
};

export default CardFacturation;
